import { useEffect, useState } from "react";
import { Compass as CompassIcon, ArrowLeft, Printer, Loader2, Check, ArrowRight, Copy, Download, RotateCcw, FileText } from "lucide-react";
import { getEpisodeReview, downloadParagraphDocx } from "@/lib/api";
import { cMyDevelopmentBySession } from "@/lib/api";
import DevelopmentReport from "@/components/DevelopmentReport";
import ReviewCurrentStructure from "@/components/ReviewCurrentStructure";

// ── Deterministic developmental review (Phase 2) ────────────────────────────
// The Writing Studio produces TWO outputs: (1) the writing product — the final paragraph,
// and (2) the developmental record. This screen keeps two concepts SEPARATE:
//   • BEFORE → AFTER + WHAT CHANGED = a FAITHFUL textual comparison (every change),
//     rendered from the deterministic `final_diff` computed on the backend.
//   • IMPORTANT MOVES = an instructional reflection, grounded ONLY in stored
//     developmental evidence (never a generic invented rationale).
// No LLM — everything is derived from the deterministic episode-review payload.

// PART F — one concise, grounded writing move.
function writingMove(data) {
  const tl = (data?.see_development?.throughline || []).filter(Boolean);
  if (tl.length >= 2) return "Building a causal chain that connects one idea to the next, so a reader can follow how you reach your conclusion.";
  const practiced = (data?.round1?.practiced || []).filter(Boolean);
  if (practiced.length) return practiced[0];
  return "Developing your idea so a reader can follow and believe it.";
}

// Faithful diff renderer — additions (green), deletions (struck, muted rose),
// replacements (old struck + new marked amber). Whitespace/equal runs verbatim.
function renderDiff(segments, keyPrefix = "d") {
  return (segments || []).map((s, i) => {
    if (s.op === "equal") return <span key={`${keyPrefix}-${i}`}>{s.text}</span>;
    if (s.op === "insert")
      return (
        <mark key={`${keyPrefix}-${i}`} className="bg-emerald-100 text-stone-900 rounded-[3px] px-0.5 box-decoration-clone" data-testid={`diff-insert-${i}`}>
          {s.text}
        </mark>
      );
    if (s.op === "delete")
      return (
        <del key={`${keyPrefix}-${i}`} className="text-stone-400 bg-rose-50 rounded-[3px] px-0.5 decoration-rose-300 box-decoration-clone" data-testid={`diff-delete-${i}`}>
          {s.text}
        </del>
      );
    if (s.op === "replace")
      return (
        <span key={`${keyPrefix}-${i}`} data-testid={`diff-replace-${i}`}>
          <del className="text-stone-400 bg-rose-50 rounded-[3px] px-0.5 decoration-rose-300 box-decoration-clone">{s.before}</del>
          <mark className="bg-amber-100 text-stone-900 rounded-[3px] px-0.5 box-decoration-clone">{s.after}</mark>
        </span>
      );
    return null;
  });
}

export default function EpisodeReview({ sessionId, onBack, onNewSession, onContinueSentenceCraft, fetcher, demo = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copiedP, setCopiedP] = useState(false);
  const [savedP, setSavedP] = useState(false);
  const [savedR, setSavedR] = useState(false);
  // The intermediate "all stages complete" screen must appear only ONCE per session. EpisodeReview
  // remounts whenever the learner revisits a completed stage and returns, so persist the dismissal in
  // sessionStorage (keyed by session) — otherwise the full-screen overlay re-blocks the stage stepper on
  // every return, making multi-stage revisit awkward.
  const _introKey = sessionId ? `tws_final_intro_${sessionId}` : "";
  const [introDismissed, setIntroDismissed] = useState(() => {
    if (demo) return true; // marketing demo opens straight into the review (no "all stages complete" gate)
    try { return _introKey ? sessionStorage.getItem(_introKey) === "1" : false; } catch (e) { return false; }
  });
  const dismissIntro = () => {
    try { if (_introKey) sessionStorage.setItem(_introKey, "1"); } catch (e) { /* ignore */ }
    setIntroDismissed(true);
  };
  const [finished, setFinished] = useState(false);
  const [continuing, setContinuing] = useState(false);
  // Best-effort WITHIN-ASSIGNMENT developmental record (Writing + Organization of Thinking) for the
  // caller's own classroom submission. Silently absent for the public/anonymous demo or non-classroom
  // sessions (401/404 → null), and may lag briefly while completion analysis runs in the background.
  const [dev, setDev] = useState(null);
  useEffect(() => {
    if (demo || !sessionId) return;
    let live = true;
    cMyDevelopmentBySession(sessionId).then((d) => live && setDev(d)).catch(() => {});
    return () => { live = false; };
  }, [sessionId, demo]);

  useEffect(() => {
    // PUBLIC MARKETING DEMO uses a demo-only fetcher (safe seeded data, NO session id → cannot query
    // arbitrary/real sessions). Authenticated review keeps the default per-session fetch. Same component.
    if (fetcher) { fetcher().then(setData).catch(() => setError(true)); return; }
    if (!sessionId) return;
    getEpisodeReview(sessionId).then(setData).catch(() => setError(true));
  }, [sessionId, fetcher]);

  const assignment = data?.assignment || "";
  const finalText = data ? data.final_draft || data.initial_draft || "" : "";
  const initialText = data?.initial_draft || "";
  const sd = data?.see_development || {};
  const diff = data?.final_diff || [];
  // REPAIR 1/12 — context-sensitive document terminology. Prefer the backend product signal; fall back to
  // counting paragraph blocks in the final text. Whole multi-paragraph product → "essay/writing"; single → "paragraph".
  const cs = data?.current_structure || null;
  const isEssay = cs ? !!cs.is_essay
    : ((finalText || "").trim().split(/\n\s*\n/).filter((b) => b.trim()).length >= 2);
  const U = isEssay ? "essay" : "paragraph";      // lowercase inline noun
  const Uc = isEssay ? "Essay" : "Paragraph";     // capitalized (button labels)
  const changed = data ? (data.final_changed ?? !!(finalText && initialText && finalText !== initialText)) : false;
  const changeCount = diff.filter((s) => s.op !== "equal").length;
  const moves = (sd.what_developed || []).filter(Boolean);
  const move = data ? writingMove(data) : "";
  const scDone = !!data?.round2;
  const pfDone = !!data?.proofreading_selected;
  const completedStages = ["Composition", ...(scDone ? ["Sentence Craft"] : []), ...(pfDone ? ["Proofreading"] : [])];
  const stagesPhrase =
    completedStages.length === 1
      ? completedStages[0]
      : completedStages.slice(0, -1).join(", ") + (completedStages.length > 2 ? "," : "") + " and " + completedStages[completedStages.length - 1];
  // Grounded summary only — never claims specific instructional meaning that the evidence can't support.
  const whatChanged =
    sd.what_changed ||
    (changed ? `You revised your ${U} in ${changeCount} place${changeCount === 1 ? "" : "s"} across the session.` : "");

  // ── Exports ──────────────────────────────────────────────────────────────
  const download = (filename, text) => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildParagraphText = () =>
    [assignment ? `ASSIGNMENT\n${assignment}` : "", `FINAL ${Uc.toUpperCase()}\n${finalText}`].filter(Boolean).join("\n\n").trim();

  const buildReviewText = () => {
    const lines = [];
    if (assignment) lines.push(`ASSIGNMENT\n${assignment}`, "");
    lines.push(`FINAL ${Uc.toUpperCase()}\n${finalText}`, "");
    if (changed) {
      lines.push("BEFORE → AFTER");
      lines.push(`Where you started:\n${initialText}`, "");
      lines.push(`Where you ended:\n${finalText}`, "");
    }
    if (moves.length) {
      lines.push("IMPORTANT MOVES YOU DEVELOPED:");
      moves.forEach((m) => lines.push(`  • ${m}`));
      lines.push("");
    }
    lines.push(`THE WRITING MOVE YOU DEVELOPED\n${move}`, "");
    lines.push("NEXT STEP\n" + (scDone ? "You completed both writing development and Sentence Craft." : "Sentence Craft — making the sentences themselves clearer and stronger — or finish."));
    return lines.join("\n").trim();
  };

  // Nicely-formatted HTML body for the emailed report (inline CSS only for email-client compatibility).
  const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const buildReviewHtml = () => {
    const sec = (label, body) =>
      `<p style="margin:18px 0 4px;font:600 12px/1.4 Arial,sans-serif;color:#8C3A2A;text-transform:uppercase;letter-spacing:0.08em">${esc(label)}</p>` +
      `<p style="margin:0;font:15px/1.6 Georgia,serif;color:#1c1917;white-space:pre-wrap">${esc(body)}</p>`;
    let h = `<div style="max-width:640px;margin:0 auto;padding:24px;background:#faf9f6">`;
    h += `<h1 style="font:600 22px/1.3 Georgia,serif;color:#1c1917;margin:0 0 4px">Your The Writing Studio writing report</h1>`;
    h += `<p style="font:13px/1.5 Arial,sans-serif;color:#78716c;margin:0 0 8px">Your final ${U} and developmental review. The full review is also attached as a Word (.docx) document.</p>`;
    if (assignment) h += sec("Assignment", assignment);
    h += sec(`Final ${U}`, finalText);
    if (changed) {
      h += sec("Where you started", initialText);
      h += sec("Where you ended", finalText);
    }
    if (moves.length) {
      h += `<p style="margin:18px 0 4px;font:600 12px/1.4 Arial,sans-serif;color:#8C3A2A;text-transform:uppercase;letter-spacing:0.08em">Important moves you developed</p>`;
      h += `<ul style="margin:0;padding-left:20px;font:15px/1.6 Georgia,serif;color:#1c1917">`;
      moves.forEach((m) => (h += `<li style="margin-bottom:6px">${esc(m)}</li>`));
      h += `</ul>`;
    }
    h += sec("The writing move you developed", move);
    h += `</div>`;
    return h;
  };


  const copyParagraph = async () => {
    try {
      await navigator.clipboard.writeText(finalText);
      setCopiedP(true);
      setTimeout(() => setCopiedP(false), 2000);
    } catch (e) {
      /* clipboard unavailable */
    }
  };
  const saveParagraph = async () => {
    try {
      await downloadParagraphDocx(finalText, assignment || "", "compass-final-paragraph.docx");
    } catch (e) {
      download("compass-final-paragraph.txt", buildParagraphText());  // fallback if export fails
    }
    setSavedP(true);
    setTimeout(() => setSavedP(false), 2000);
  };
  const saveReview = async () => {
    try {
      await downloadParagraphDocx(buildReviewText(), "Developmental Review", "compass-developmental-review.docx");
    } catch (e) {
      download("compass-developmental-review.txt", buildReviewText());  // fallback if export fails
    }
    setSavedR(true);
    setTimeout(() => setSavedR(false), 2000);
  };
  const printWith = (paragraphOnly) => {
    const el = document.documentElement;
    if (paragraphOnly) el.classList.add("print-paragraph-only");
    const cleanup = () => {
      el.classList.remove("print-paragraph-only");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 1000);
  };

  const btn =
    "inline-flex items-center gap-1.5 border border-stone-300 text-stone-700 text-[12.5px] px-3 py-1.5 rounded-sm hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors";

  return (
    <div className="flex-1 flex flex-col py-4 w-full" data-testid="episode-review">
      {/* Top bar — back + terminal action */}
      <div className="flex items-center justify-between no-print">
        {onBack ? (
          <button
            onClick={onBack}
            data-testid="episode-review-back"
            className="inline-flex items-center gap-2 text-stone-500 hover:text-[#8C3A2A] transition-colors font-mono-panel text-[11px] uppercase tracking-[0.16em]"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : (
          <span />
        )}
      </div>

      {error ? (
        <p className="mt-10 text-stone-500" data-testid="episode-review-error">
          This review isn't available for this session.
        </p>
      ) : !data ? (
        <div className="mt-10 flex items-center gap-2 text-stone-500" data-testid="episode-review-loading">
          <Loader2 className="h-4 w-4 animate-spin text-[#8C3A2A]" />
          <span className="font-mono-panel text-[12px] uppercase tracking-[0.16em]">Assembling your {U}…</span>
        </div>
      ) : !introDismissed ? (
        <div data-testid="final-transition" className="fixed inset-0 z-50 flex items-center justify-center bg-[#faf9f6]/95 backdrop-blur-sm px-6">
          <div className="w-full max-w-md bg-white border border-stone-200 rounded-md shadow-xl p-8 text-center">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100">
              <Check className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="font-mono-panel text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-1">All stages complete</p>
            <h2 className="font-serif-display text-[1.5rem] leading-tight text-stone-900 mb-2">
              You have completed {stagesPhrase}.
            </h2>
            <p className="text-[14px] text-stone-600 mb-6">The next page contains the final summary of your progress.</p>
            <button
              onClick={dismissIntro}
              data-testid="final-transition-continue"
              className="group inline-flex items-center gap-2.5 bg-[#8C3A2A] text-white px-7 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors"
            >
              View my final summary
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-6" data-testid="episode-review-detail">
          {/* ── ASSIGNMENT (shown first, right under the stage stepper) ── */}
          {assignment && (
            <div data-testid="review-assignment">
              <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400">Assignment</p>
              <p className="mt-1 text-[14px] text-stone-800">{assignment}</p>
            </div>
          )}
          {/* ── ACTION GROUPS ── (hidden in the public marketing demo: no copy/print/save/email of demo data,
              and no backend export/email side-effects from an anonymous page) */}
          {!demo && (
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-8 no-print">
            <div data-testid="writing-product-actions">
              <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-1.5">Writing product</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={copyParagraph} data-testid="copy-paragraph" className={btn}>
                  <Copy className="h-3.5 w-3.5" /> {copiedP ? "Copied" : `Copy ${Uc}`}
                </button>
                <button onClick={() => printWith(true)} data-testid="print-paragraph" className={btn}>
                  <Printer className="h-3.5 w-3.5" /> Print {Uc}
                </button>
                <button onClick={saveParagraph} data-testid="save-paragraph" className={btn}>
                  <Download className="h-3.5 w-3.5" /> {savedP ? "Saved" : `Save ${Uc}`}
                </button>
                {data && onNewSession && (
                  <button
                    onClick={onNewSession}
                    data-testid="episode-review-new-session"
                    className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12.5px] px-3 py-1.5 rounded-sm hover:bg-[#6B2C20] transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> New {U}
                  </button>
                )}
              </div>
            </div>
            <div data-testid="developmental-review-actions">
              <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-1.5">Developmental review</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => printWith(false)} data-testid="print-review" className={btn}>
                  <Printer className="h-3.5 w-3.5" /> Print Review
                </button>
                <button onClick={saveReview} data-testid="save-review" className={btn}>
                  <Download className="h-3.5 w-3.5" /> {savedR ? "Saved" : "Save Review"}
                </button>
              </div>
            </div>
          </div>
          )}

          {/* ── FINAL PARAGRAPH — first-class artifact ── */}
          <div className="rounded-lg border-2 border-[#8C3A2A]/25 bg-white p-6 sm:p-7 shadow-sm" data-testid="final-paragraph">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-[#8C3A2A]" />
              <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A]">Final {U}</p>
            </div>
            <p className="text-[16px] text-stone-900 leading-relaxed whitespace-pre-wrap font-serif-display" data-testid="final-paragraph-text">
              {finalText}
            </p>
          </div>

          {/* ── WITHIN-ASSIGNMENT DEVELOPMENT (Writing + Organization of Thinking) — classroom submissions
              only; absent for the public demo / non-classroom sessions. ── */}
          {dev && !demo && <DevelopmentReport data={dev} embedded />}

          {/* ── DEVELOPMENTAL REVIEW (review-only: excluded from Print Paragraph) ── */}
          <div className="review-only space-y-6">
            <div>
              <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 mt-2 mb-1">
                Developmental record
              </p>
              <h2 className="font-serif-display text-2xl sm:text-3xl leading-snug text-stone-900">What you developed</h2>
            </div>

            {/* 1 · WHAT CHANGED (grounded summary) */}
            {whatChanged && (
              <div data-testid="review-what-changed">
                <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-1.5">What changed</p>
                <p className="text-[15px] text-stone-900 leading-relaxed">{whatChanged}</p>
              </div>
            )}

            {/* 2 · BEFORE → AFTER (faithful comparison) or NO-REVISION */}
            {changed ? (
              <div className="rounded-lg border-2 border-[#8C3A2A]/30 bg-gradient-to-b from-[#fbf6f2] to-white p-6 sm:p-8 shadow-sm" data-testid="review-before-after">
                <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-4">Before → After</p>
                <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-3 items-stretch">
                  <div className="rounded-md border border-stone-200 bg-white p-4" data-testid="review-before">
                    <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400 mb-2">Where you started</p>
                    <p className="text-[14px] text-stone-700 leading-relaxed whitespace-pre-wrap font-serif-display">{initialText}</p>
                  </div>
                  <div className="hidden md:flex items-center justify-center text-[#8C3A2A]">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                  <div className="rounded-md border border-[#8C3A2A]/25 bg-[#faf9f6] p-4" data-testid="review-after">
                    <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A] mb-2">Where you ended</p>
                    <p className="text-[14px] text-stone-900 leading-relaxed whitespace-pre-wrap font-serif-display" data-testid="review-after-text">
                      {finalText}
                    </p>
                  </div>
                </div>

                {/* WHAT CHANGED — faithful, deterministic diff (every change) */}
                {diff.length > 0 && (
                  <div className="mt-6" data-testid="review-diff">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                      <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400">The changes you made</p>
                      <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><span className="inline-block h-2.5 w-2.5 rounded-[2px] bg-emerald-100 border border-emerald-200" /> added</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><span className="inline-block h-2.5 w-2.5 rounded-[2px] bg-amber-100 border border-amber-200" /> replaced</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><span className="inline-block h-2.5 w-2.5 rounded-[2px] bg-rose-50 border border-rose-200" /> removed</span>
                    </div>
                    <p className="text-[14px] text-stone-800 leading-relaxed whitespace-pre-wrap font-serif-display rounded-md border border-stone-200 bg-white p-4" data-testid="review-diff-text">
                      {renderDiff(diff)}
                    </p>
                  </div>
                )}

                {/* IMPORTANT MOVES YOU DEVELOPED — instructional reflection, grounded only */}
                {moves.length > 0 && (
                  <div className="mt-6 space-y-3" data-testid="review-moves">
                    <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400">Important moves you developed</p>
                    {moves.map((m, i) => (
                      <div key={i} className="rounded-md border border-emerald-100 bg-emerald-50/50 p-4 flex items-start gap-2" data-testid={`review-move-${i}`}>
                        <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        <p className="text-[14px] text-stone-900 leading-snug" data-testid={`review-move-text-${i}`}>{m}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-stone-200 bg-[#faf9f6] p-6" data-testid="review-no-revision">
                <div className="mb-4">
                  <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A] mb-1.5">What you worked on</p>
                  <p className="text-[15px] text-stone-900 leading-relaxed">{move}</p>
                </div>
                <div>
                  <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400 mb-1.5">What you decided</p>
                  <p className="text-[14px] text-stone-700 leading-relaxed">
                    After reviewing the {U}, you decided it already expressed the idea clearly enough for your purpose.
                  </p>
                </div>
              </div>
            )}

            {/* REPAIR 11 · CURRENT STRUCTURAL ASSESSMENT — how THIS product/thinking is organized now
                 (independent of any before→after developmental comparison). No grade, no ability claim. */}
            <ReviewCurrentStructure cs={cs} U={U} />

            {/* 3 · THE WRITING MOVE YOU DEVELOPED */}
            <div data-testid="review-writing-move">
              <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-1.5">The writing move you developed</p>
              <p className="text-[15px] text-stone-900 leading-relaxed">{move}</p>
            </div>

            {/* 4 · NEXT STEP */}
            <div className="rounded-md border border-[#8C3A2A]/25 bg-[#fbf6f2] p-5" data-testid="review-next-step">
              <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-1.5">Next step</p>
              {scDone ? (
                <p className="text-[14px] text-stone-700 leading-relaxed">
                  You completed both writing development and Sentence Craft. Your {U} and record are ready above.
                </p>
              ) : finished ? (
                <p className="text-[14px] text-stone-700 leading-relaxed" data-testid="review-finished-note">
                  You're all set — your writing product and developmental record are above to copy, print, or save.
                </p>
              ) : (
                <>
                  <p className="text-[14px] text-stone-700 leading-relaxed mb-4">
                    Your ideas are now connected. The next stage is <span className="text-stone-900 font-medium">Sentence Craft</span> — making the
                    sentences themselves clearer and stronger.
                  </p>
                  <div className="flex flex-wrap gap-2 no-print">
                    {onContinueSentenceCraft && (
                      <button
                        onClick={() => { setContinuing(true); onContinueSentenceCraft(); }}
                        disabled={continuing}
                        data-testid="review-continue-sentence-craft"
                        className="inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-5 py-2.5 rounded-sm font-medium hover:bg-[#6B2C20] transition-colors disabled:opacity-70"
                      >
                        {continuing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Preparing Sentence Craft…
                          </>
                        ) : (
                          <>
                            Continue to Sentence Craft <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setFinished(true)}
                      disabled={continuing}
                      data-testid="review-finish"
                      className="inline-flex items-center gap-2 border border-stone-300 text-stone-700 px-5 py-2.5 rounded-sm hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-50"
                    >
                      Finish
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-stone-400 font-mono-panel text-[10px] uppercase tracking-[0.16em] no-print pt-2">
            <CompassIcon className="h-3.5 w-3.5" /> The Writing Studio
          </div>
        </div>
      )}
    </div>
  );
}
